import { useEffect, useRef, useState } from "react";

// primereact component
import { Toast } from "primereact/toast";
import { Toolbar } from "primereact/toolbar";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Image } from "primereact/image";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";

// master tabs
import { MasterTabs } from "../MasterTabs";

// datatable options
import {
  defaultPageRowOptions,
  defaultRowOptions,
  paginatorLinks,
  showingEntries,
  SkeletonbodyTemplate,
  Skeletonitems,
} from "../../../appconfig/Settings";
import { serviceListColumn } from "../../../appconfig/DataTableColumns";

// api from services
import { getAllDetails } from "../../../service/ProductService";

// api constant
import { GET_SERVICE_LIST } from "../../../config/ApiConstant";
import { globalNameFilterTemplate } from "../../../appconfig/AppHelper";

// interfavce
import { useNavigate } from "react-router";
import {
  amountTemplate,
  globalFieldTemplate,
} from "../../../appconfig/TemplateSetting";
import { Tag } from "primereact/tag";

const Services = () => {
  document.title = "Services | Agriown";

  const navigate = useNavigate();

  //Set Toast/ Filter Properties
  const toast = useRef<any>(null);
  const [globalFilter, setGlobalFilter] = useState<any>(null);

  //service list state
  const [serviceList, setServiceList] = useState<any>([]);

  //Loading/Page Loading
  const [pageLoad, setPageLoad] = useState<boolean>(false);

  // datatable filter value sets
  const emptyFilters = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    service_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    price: { value: null, matchMode: FilterMatchMode.EQUALS },
    description: { value: null, matchMode: FilterMatchMode.CONTAINS },
    type_of_equipment: { value: null, matchMode: FilterMatchMode.CONTAINS },
    price_on: { value: null, matchMode: FilterMatchMode.CONTAINS },
  };
  const [filters, setFilters] = useState(emptyFilters);

  // useEffect to get service list data on page load
  useEffect(() => {
    getServiceList();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // get service list api
  const getServiceList = async () => {
    try {
      const response = await getAllDetails(GET_SERVICE_LIST);

      if (response) {
        setServiceList(response);
        setPageLoad(true);
      } else {
        setServiceList([]);
        setPageLoad(false);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
      setPageLoad(false);
    }
  };

  // description template
  const descriptionTemplate = (rawData: any) => {
    return (
      <Button
        className="bg-authdesc"
        label={rawData?.description}
        tooltip={rawData?.description}
        tooltipOptions={{ position: "bottom" }}
      />
    );
  };

  // global search or add service button
  const leftToolbarTemplate = () => {
    return (
      <>
        <span className="mr-0 md:mr-3 search-bar">
          <i className="pi pi-search"></i>
          <InputText
            type="search"
            className="search-input"
            value={globalFilter}
            onInput={(e) => setGlobalFilter(e.currentTarget.value)}
            placeholder="Search..."
          />
        </span>
      </>
    );
  };

  //right toolbar
  const rightToolbarTemplate = () => {
    return (
      <>
        <Button
          icon="pi pi-filter-slash"
          label="Clear"
          onClick={() => clearAll()}
          className="mr-0 md:mr-2 p-button-outlined p-button-primary"
        ></Button>
        <Button
          label="Add Service"
          icon="pi pi-plus"
          className="ml-2 p-button toolbar-button"
          onClick={() => navigate("/master/services/add-service")}
        />
      </>
    );
  };

  //Clear All Filter
  const clearAll = () => {
    setFilters({ ...emptyFilters });
    setGlobalFilter("");
  };

  //Change action Template
  const actionBodyTemplate = (rowData: any) => (
    <div className="actions">
      <Button
        icon="pi pi-pencil"
        className="mr-2 p-button-secondary"
        tooltip="Edit"
        tooltipOptions={{ position: "top" }}
        onClick={() =>
          navigate(`/master/services/edit-service/${btoa(rowData?.id)}`)
        }
      />
    </div>
  );

  // photo template
  const photoTemplate = (rowData: any) => {
    return rowData.photo ? (
      <Image
        className="image-preview"
        src={rowData.photo}
        alt="Image"
        width="40"
        preview
      />
    ) : (
      <span className="text-muted"></span>
    );
  };

  // Body template for type_of_equipment
  const typeOfEquipmentBodyTemplate = (rowData: any) => {
    const values = !window.cn(rowData.type_of_equipment)
      ? rowData.type_of_equipment.split(",").map((item: any) => item.trim())
      : [];

    return (
      <div className="flex justify-content-center flex-wrap gap-1">
        {!window.cn(values)
          ? values.map((val: any, index: any) => (
              <Tag key={index} value={val} severity="warning" />
            ))
          : "-"}
      </div>
    );
  };

  return (
    <>
      <div className="grid crud-demo">
        <div className="col-12">
          <div className="card">
            <MasterTabs index={0} />
            <Toast ref={toast} />

            <Toolbar
              left={leftToolbarTemplate}
              right={rightToolbarTemplate}
            ></Toolbar>

            {/* Datatable */}
            {pageLoad ? (
              <>
                <DataTable
                  value={serviceList}
                  showGridlines
                  stripedRows
                  globalFilter={globalFilter || null}
                  globalFilterFields={['service_name', 'description', 'type_of_equipment', 'price_on', 'price']}
                  rows={defaultRowOptions}
                  paginator={serviceList.length >= 10 && true}
                  rowsPerPageOptions={defaultPageRowOptions}
                  className="datatable-responsive p-datatble-sm"
                  paginatorTemplate={paginatorLinks}
                  currentPageReportTemplate={showingEntries}
                  emptyMessage="No Data Found"
                  filters={{...filters, global: { ...filters.global, value: globalFilter || null }}}
                  onFilter={(e: any) => setFilters(e.filters)}
                  removableSort
                >
                  {serviceListColumn.map((col, i) => {
                    if (col.field === "action") {
                      return (
                        <Column
                          key={col.field}
                          field={col.field}
                          header={col.header}
                          body={actionBodyTemplate}
                        />
                      );
                    } else if (col.field === "photo") {
                      return (
                        <Column
                          key={col.field}
                          field={col.field}
                          header={col.header}
                          body={photoTemplate}
                        />
                      );
                    } else if (col.field === "service_name") {
                      return (
                        <Column
                          key={col.field}
                          field={col.field}
                          sortable
                          filter
                          body={globalFieldTemplate}
                          filterElement={globalNameFilterTemplate}
                          header={col.header}
                        />
                      );
                    } else if (col.field === "type_of_equipment") {
                      return (
                        <Column
                          key={col.field}
                          field={col.field}
                          body={typeOfEquipmentBodyTemplate}
                          header={col.header}
                        />
                      );
                    } else if (col.field === "price_on") {
                      return (
                        <Column
                          key={col.field}
                          field={col.field}
                          sortable
                          filter
                          filterElement={globalNameFilterTemplate}
                          header={col.header}
                        />
                      );
                    } else if (col.field === "price") {
                      return (
                        <Column
                          key={col.field}
                          field={col.field}
                          sortable
                          body={amountTemplate}
                          header={col.header}
                        />
                      );
                    } else if (col.field === "operator_price") {
                      return (
                        <Column
                          key={col.field}
                          field={col.field}
                          sortable
                          body={amountTemplate}
                          header={col.header}
                        />
                      );
                    } else if (col.field === "description") {
                      return (
                        <Column
                          key={col.field}
                          field={col.field}
                          body={descriptionTemplate}
                          sortable
                          header={col.header}
                        />
                      );
                    } else {
                      return (
                        <Column
                          key={col.field}
                          sortable
                          field={col.field}
                          header={col.header}
                        />
                      );
                    }
                  })}
                </DataTable>
              </>
            ) : (
              <>
                {/* Skeleton Data table */}
                <DataTable value={Skeletonitems}>
                  {serviceListColumn.map((col, i) => (
                    <Column
                      key={col.field}
                      field={col.field}
                      header={col.header}
                      body={SkeletonbodyTemplate}
                    />
                  ))}
                </DataTable>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Services;
